import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import ChapterHeading from "./ChapterHeading";
import ImageReveal from "./ImageReveal";
import Reveal from "./Reveal";
import TradifyForm from "./TradifyForm";
import CTABand from "./CTABand";
import { QuoteButton, CallButton } from "./Buttons";
import useGallery from "@/hooks/useGallery";
import MultiRailPromo from "./MultiRailPromo";

export default function ServicePageLayout({
  pageTestid,
  metaTitle,
  eyebrow,
  title,
  sub,
  heroImg,
  heroAlt,
  enquiryTitle,
  enquiryCopy,
  service,
  applications,
  applicationsTitle,
  note,
  galleryCategory,
  ctaTitle,
  ctaLabel,
  heroCtaLabel = "ENQUIRE TODAY",
  promo = null,
}) {
  const reduce = useReducedMotion();
  const allGalleryItems = useGallery();
  const galleryItems = allGalleryItems.filter((i) => i.category === galleryCategory).slice(0, 4);

  return (
    <div data-testid={pageTestid}>
      <section className="relative overflow-hidden bg-ivory">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-44 sm:px-6 lg:px-8 lg:pb-24 lg:pt-40">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 20 }}
                animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center gap-4">
                  <span className="h-px w-10 bg-sage" aria-hidden="true" />
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">{eyebrow}</span>
                </div>
                <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
                  {title}
                </h1>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/75 sm:text-lg">{sub}</p>
                <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                  <QuoteButton
                    label={heroCtaLabel}
                    service={service}
                    testid={`${pageTestid}-hero-quote-button`}
                  />
                  <CallButton />
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-5">
              <ImageReveal delay={0.15} radius="2rem" className="overflow-hidden rounded-[2rem] border border-line shadow-xl">
                <img
                  src={heroImg}
                  alt={heroAlt}
                  className="aspect-[4/3] w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                />
              </ImageReveal>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-sand">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            <Reveal className="lg:col-span-5">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">Enquire now</p>
              <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight text-charcoal sm:text-4xl">
                {enquiryTitle}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-ink/75 sm:text-lg">{enquiryCopy}</p>
              <ul className="mt-6 space-y-3">
                {["Simple solutions. Professional installation.", "Local service across Perth.", "Send a photo — we'll take it from there."].map(
                  (point) => (
                    <li key={point} className="flex items-start gap-3 text-sm font-semibold text-ink/80">
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sage" aria-hidden="true" />
                      {point}
                    </li>
                  )
                )}
              </ul>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <div className="rounded-3xl border border-line bg-white p-3 shadow-lg sm:p-4">
                <TradifyForm testid={`${pageTestid}-enquiry`} />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <ChapterHeading num="01" eyebrow="Where we install" title={applicationsTitle} testid={`${pageTestid}-applications`} />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {applications.map((app, i) => (
              <Reveal key={app.title} delay={i * 0.06}>
                <div className="group h-full rounded-3xl border border-line bg-white p-7 transition-colors duration-300 hover:border-sage">
                  <app.icon className="h-7 w-7 text-sage-dark transition-colors duration-300 group-hover:text-sage" aria-hidden="true" />
                  <h3 className="mt-5 font-display text-lg font-bold text-charcoal">{app.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/70">{app.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          {note && (
            <Reveal delay={0.15}>
              <p className="mt-10 max-w-3xl rounded-2xl border border-line bg-sage-light p-5 text-sm font-semibold leading-relaxed text-sage-dark">
                {note}
              </p>
            </Reveal>
          )}
        </div>
      </section>

      {promo && <MultiRailPromo variant="full" {...promo} />}

      <section className="border-t border-line bg-ivory">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <ChapterHeading num="02" eyebrow="Recent installations" title="Real work. Real Perth homes." testid={`${pageTestid}-gallery-heading`} />
            <Reveal delay={0.1}>
              <Link
                to="/gallery"
                data-testid={`${pageTestid}-gallery-link`}
                className="inline-flex min-h-[48px] items-center gap-2 rounded-full border-2 border-charcoal px-6 py-2.5 text-xs font-bold tracking-[0.12em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-white"
              >
                VIEW OUR GALLERY
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {galleryItems.map((item, i) => (
              <ImageReveal key={item.src} delay={i * 0.06} radius="1rem">
                <Link
                  to="/gallery"
                  data-testid={`${pageTestid}-gallery-image-${i}`}
                  className="group block overflow-hidden rounded-2xl border border-line"
                  aria-label={`${item.alt} — open gallery`}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </Link>
              </ImageReveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand title={ctaTitle} quoteLabel={ctaLabel} service={service} testid={`${pageTestid}-cta`} />
    </div>
  );
}
